import { supabaseCustomRole } from "@/lib/supabase/customRoleClient";

export interface CityStats {
  totalDealers: number;
  averageRating: number;
  verifiedDealers: number;
  averageSpread: number;
  topRatedDealer?: {
    name: string;
    rating: number;
  };
}

/**
 * Calculate statistics for a specific city
 */
export const calculateCityStats = async (city: string): Promise<CityStats> => {
  try {
    console.log(`[SEO] Calculating stats for city: ${city}`);
    
    // Fetch dealers for the city with ratings
    const { data: dealers, error } = await supabaseCustomRole
      .from("vw_dealers_with_ratings")
      .select("*")
      .ilike("city", `%${city}%`);

    if (error) {
      console.error("[SEO] Error fetching dealers for stats:", error);
      return getDefaultStats();
    }

    if (!dealers || dealers.length === 0) {
      console.log(`[SEO] No dealers found for city: ${city}`);
      return getDefaultStats();
    }

    // Calculate basic stats
    const totalDealers = dealers.length;
    const verifiedDealers = dealers.filter(d => d.is_verified).length;
    
    // Calculate average rating (only for dealers with ratings)
    const dealersWithRatings = dealers.filter(d => d.average_rating && d.average_rating > 0);
    const averageRating = dealersWithRatings.length > 0 
      ? dealersWithRatings.reduce((sum, d) => sum + parseFloat(d.average_rating), 0) / dealersWithRatings.length
      : 0;

    // Find top rated dealer
    const topRatedDealer = dealersWithRatings.length > 0
      ? dealersWithRatings.reduce((top, current) => 
          parseFloat(current.average_rating) > parseFloat(top.average_rating) ? current : top
        )
      : null;

    // Fetch quotation data to calculate average spread
    const dealerIds = dealers.map(d => d.id);
    const { data: quotations } = await supabaseCustomRole
      .from('dealer_quotations')
      .select('spread_type, spread_percentage, spread_fixed')
      .in('dealer_id', dealerIds)
      .eq('metal', 'ORO');

    let averageSpread = 5; // Default
    if (quotations && quotations.length > 0) {
      const spreads = quotations.map(q => 
        q.spread_type === 'FIXED' ? q.spread_fixed : q.spread_percentage
      ).filter(s => s > 0);
      
      if (spreads.length > 0) {
        averageSpread = spreads.reduce((sum, spread) => sum + spread, 0) / spreads.length;
      }
    }

    const stats: CityStats = {
      totalDealers,
      averageRating: Math.round(averageRating * 10) / 10,
      verifiedDealers,
      averageSpread: Math.round(averageSpread * 10) / 10,
      topRatedDealer: topRatedDealer ? {
        name: topRatedDealer.name,
        rating: Math.round(parseFloat(topRatedDealer.average_rating) * 10) / 10
      } : undefined
    };

    console.log(`[SEO] Calculated stats for ${city}:`, stats);
    return stats;
  } catch (error) {
    console.error("[SEO] Error calculating city stats:", error);
    return getDefaultStats();
  }
};

/**
 * Calculate national statistics (all dealers)
 */
export const calculateNationalStats = async (): Promise<CityStats> => {
  try {
    console.log("[SEO] Calculating national stats");
    
    // Fetch all dealers with ratings
    const { data: dealers, error } = await supabaseCustomRole
      .from("vw_dealers_with_ratings")
      .select("*");

    if (error) {
      console.error("[SEO] Error fetching dealers for national stats:", error);
      return getDefaultStats();
    }

    if (!dealers || dealers.length === 0) {
      return getDefaultStats();
    }

    // Calculate stats same as city but for all dealers
    const totalDealers = dealers.length;
    const verifiedDealers = dealers.filter(d => d.is_verified).length;
    
    const dealersWithRatings = dealers.filter(d => d.average_rating && d.average_rating > 0);
    const averageRating = dealersWithRatings.length > 0 
      ? dealersWithRatings.reduce((sum, d) => sum + parseFloat(d.average_rating), 0) / dealersWithRatings.length
      : 0;

    const topRatedDealer = dealersWithRatings.length > 0
      ? dealersWithRatings.reduce((top, current) => 
          parseFloat(current.average_rating) > parseFloat(top.average_rating) ? current : top
        )
      : null;

    // Calculate average spread nationally
    const { data: quotations } = await supabaseCustomRole
      .from('dealer_quotations')
      .select('spread_type, spread_percentage, spread_fixed')
      .eq('metal', 'ORO');

    let averageSpread = 5;
    if (quotations && quotations.length > 0) {
      const spreads = quotations.map(q => 
        q.spread_type === 'FIXED' ? q.spread_fixed : q.spread_percentage
      ).filter(s => s > 0);
      
      if (spreads.length > 0) {
        averageSpread = spreads.reduce((sum, spread) => sum + spread, 0) / spreads.length;
      }
    }

    const stats: CityStats = {
      totalDealers,
      averageRating: Math.round(averageRating * 10) / 10,
      verifiedDealers,
      averageSpread: Math.round(averageSpread * 10) / 10,
      topRatedDealer: topRatedDealer ? {
        name: topRatedDealer.name,
        rating: Math.round(parseFloat(topRatedDealer.average_rating) * 10) / 10
      } : undefined
    };

    console.log("[SEO] Calculated national stats:", stats);
    return stats;
  } catch (error) {
    console.error("[SEO] Error calculating national stats:", error);
    return getDefaultStats();
  }
};

/**
 * Get top cities by dealer count
 */
export const getTopCitiesByDealerCount = async (limit: number = 10): Promise<Array<{city: string, count: number}>> => {
  try {
    const { data: cities, error } = await supabaseCustomRole
      .from("vw_dealers_with_ratings")
      .select("city")
      .not("city", "is", null);

    if (error || !cities) {
      console.error("[SEO] Error fetching cities:", error);
      return [];
    }

    // Count dealers per city
    const cityCount = cities.reduce((acc, dealer) => {
      const city = dealer.city?.trim();
      if (city) {
        acc[city] = (acc[city] || 0) + 1;
      }
      return acc;
    }, {} as Record<string, number>);

    // Sort and limit
    return Object.entries(cityCount)
      .sort(([,a], [,b]) => b - a)
      .slice(0, limit)
      .map(([city, count]) => ({ city, count }));
  } catch (error) {
    console.error("[SEO] Error getting top cities:", error);
    return [];
  }
};

/**
 * Default stats when data is not available
 */
const getDefaultStats = (): CityStats => ({
  totalDealers: 0,
  averageRating: 0,
  verifiedDealers: 0,
  averageSpread: 5
});
